import { useCallback, useRef, useState } from 'react';
import { Panel, ViewportPortal, useReactFlow } from '@xyflow/react';
import { Eraser, EyeOff, X } from 'lucide-react';
import { ANCHOS_TRAZO, COLORES_TRAZO, agregarPunto, autoresDe, cajaDe, nuevoTrazo, pathDe, trazosTocados } from '../graph/trazos';
import { inicialDe } from './comentariosContext';
import type { Trazo } from '../graph/types';

export interface EstadoDibujo {
  color: string;
  ancho: number;
  borrador: boolean;
  autoresOcultos: string[];
}

/**
 * Capa de dibujo a mano alzada. Va como hijo de <ReactFlow>:
 *  - los trazos se dibujan en un SVG dentro del `ViewportPortal` (coordenadas del canvas,
 *    se mueven y escalan con el zoom);
 *  - mientras la herramienta está activa, una capa transparente captura el puntero.
 */
export function CapaDibujo({
  trazos,
  activa,
  estado,
  onEstado,
  usuario,
  onAgregar,
  onBorrarVarios,
  onSalir,
}: {
  trazos: Trazo[];
  activa: boolean;
  estado: EstadoDibujo;
  onEstado: (patch: Partial<EstadoDibujo>) => void;
  usuario: { id: string; nombre: string };
  onAgregar: (t: Trazo) => void;
  onBorrarVarios: (ids: string[]) => void;
  onSalir: () => void;
}) {
  const rf = useReactFlow();
  const [enCurso, setEnCurso] = useState<Trazo | null>(null);
  const enCursoRef = useRef<Trazo | null>(null);

  const aFlow = useCallback((e: { clientX: number; clientY: number }) => rf.screenToFlowPosition({ x: e.clientX, y: e.clientY }), [rf]);

  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = aFlow(e);
    const t = nuevoTrazo(usuario, estado.color, estado.ancho, p.x, p.y);
    enCursoRef.current = t;
    setEnCurso(t);
  };
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!enCursoRef.current) return;
    const p = aFlow(e);
    const t = agregarPunto(enCursoRef.current, p.x, p.y, 1.5 / rf.getZoom());
    if (t !== enCursoRef.current) {
      enCursoRef.current = t;
      setEnCurso(t);
    }
  };
  const onUp = () => {
    const t = enCursoRef.current;
    enCursoRef.current = null;
    setEnCurso(null);
    if (t) onAgregar(t);
  };

  // Goma por barrido: con el botón apretado, todo trazo que pase a menos de RADIO_GOMA px
  // (de pantalla) del puntero se borra. Un click suelto también borra lo que toque.
  const RADIO_GOMA = 14;
  const borrandoRef = useRef(false);
  const borrarEn = (e: { clientX: number; clientY: number }) => {
    const p = aFlow(e);
    const ids = trazosTocados(trazos, p.x, p.y, RADIO_GOMA / rf.getZoom());
    if (ids.length) onBorrarVarios(ids);
  };
  const onDownGoma = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    borrandoRef.current = true;
    borrarEn(e);
  };
  const onMoveGoma = (e: React.PointerEvent<HTMLDivElement>) => {
    if (borrandoRef.current) borrarEn(e);
  };
  const onUpGoma = () => {
    borrandoRef.current = false;
  };

  const ocultos = new Set(estado.autoresOcultos);
  const visibles = trazos.filter((t) => !ocultos.has(t.autorId));
  const autores = autoresDe(trazos);
  // El SVG raíz se dimensiona a la caja de los trazos: Chrome no detecta clicks en lo que
  // desborda un <svg> de 1×1, y la goma no funcionaba.
  const todos = enCurso ? [...visibles, enCurso] : visibles;
  const caja = cajaDe(todos);
  const PAD = 24;
  const box = caja ? { x: caja.minX - PAD, y: caja.minY - PAD, w: caja.maxX - caja.minX + PAD * 2, h: caja.maxY - caja.minY + PAD * 2 } : null;

  return (
    <>
      <ViewportPortal>
        {box && (
          <svg
            style={{ position: 'absolute', left: box.x, top: box.y, width: box.w, height: box.h, overflow: 'visible', pointerEvents: 'none' }}
            viewBox={`${box.x} ${box.y} ${box.w} ${box.h}`}
          >
            {visibles.map((t) => (
              <path
                key={t.id}
                d={pathDe(t.puntos)}
                stroke={t.color}
                strokeWidth={t.ancho}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <title>{t.autorNombre}</title>
              </path>
            ))}
            {enCurso && <path d={pathDe(enCurso.puntos)} stroke={enCurso.color} strokeWidth={enCurso.ancho} fill="none" strokeLinecap="round" strokeLinejoin="round" />}
          </svg>
        )}
      </ViewportPortal>

      {activa && !estado.borrador && (
        <div
          className="flujos-cursor-lapiz absolute inset-0 z-[10] touch-none"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onPointerLeave={() => enCursoRef.current && onUp()}
        />
      )}
      {activa && estado.borrador && (
        <div className="flujos-cursor-goma absolute inset-0 z-[10] touch-none" onPointerDown={onDownGoma} onPointerMove={onMoveGoma} onPointerUp={onUpGoma} onPointerCancel={onUpGoma} onPointerLeave={onUpGoma} />
      )}

      {activa && (
        <Panel position="bottom-center" className="!m-0 !mb-[68px]">
          <div className="flex items-center gap-1 rounded-full border border-black/[0.06] bg-white p-1 shadow-[0_8px_24px_rgba(2,6,43,0.10)]">
            {COLORES_TRAZO.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onEstado({ color: c, borrador: false })}
                title="Color"
                className={`flex h-7 w-7 items-center justify-center rounded-full ${!estado.borrador && estado.color === c ? 'ring-2 ring-brand-primary/50 ring-offset-1' : ''}`}
              >
                <span className="h-4 w-4 rounded-full border border-black/10" style={{ background: c }} />
              </button>
            ))}
            <span className="mx-1 h-5 w-px bg-black/[0.08]" />
            {ANCHOS_TRAZO.map((a) => (
              <button key={a} type="button" onClick={() => onEstado({ ancho: a, borrador: false })} title={`Grosor ${a}`} className={`flex h-7 w-7 items-center justify-center rounded-full ${!estado.borrador && estado.ancho === a ? 'bg-[#E8EEFF]' : 'hover:bg-surface-elevated'}`}>
                <span className="rounded-full bg-[#02062B]" style={{ width: a + 4, height: a + 4 }} />
              </button>
            ))}
            <span className="mx-1 h-5 w-px bg-black/[0.08]" />
            <button type="button" onClick={() => onEstado({ borrador: !estado.borrador })} title="Goma: pasá por encima de los trazos (con el botón apretado) para borrarlos" className={`flex h-7 items-center gap-1 rounded-full px-2 text-[11.5px] font-medium ${estado.borrador ? 'bg-[#FEE7E7] text-[#B91C1C]' : 'text-neutral-600 hover:bg-surface-elevated'}`}>
              <Eraser className="h-3.5 w-3.5" /> Goma
            </button>
            {autores.length > 0 && (
              <>
                <span className="mx-1 h-5 w-px bg-black/[0.08]" />
                {autores.map((a) => {
                  const oculto = ocultos.has(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => onEstado({ autoresOcultos: oculto ? estado.autoresOcultos.filter((x) => x !== a.id) : [...estado.autoresOcultos, a.id] })}
                      title={`${oculto ? 'Mostrar' : 'Ocultar'} los ${a.cantidad} trazos de ${a.nombre}`}
                      className={`relative flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold ${oculto ? 'bg-neutral-200 text-neutral-500' : 'bg-brand-primary text-white'}`}
                    >
                      {inicialDe(a.nombre)}
                      {oculto && <EyeOff className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-white p-px text-neutral-600" />}
                    </button>
                  );
                })}
              </>
            )}
            <span className="mx-1 h-5 w-px bg-black/[0.08]" />
            <button type="button" onClick={onSalir} title="Salir de Dibujar (Esc)" className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-500 hover:bg-surface-elevated hover:text-brand-black">
              <X className="h-4 w-4" />
            </button>
          </div>
        </Panel>
      )}
    </>
  );
}
